/**
 * SentList — "Sent" sub-tab for the v2 Fax workspace.
 * Compact table listing outbound faxes with status + retry action.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw, CheckCircle2, AlertTriangle, Download } from "lucide-react";
import { faxAPI } from "./faxAPI";
import { formatPhoneNumber } from "../../../lib/utils";
import { NAVY, GREEN, RED, AMBER, NAVY_HAIRLINE, NAVY_TINT } from "../tokens";
import ViewToggle, { useViewMode } from "../ViewToggle";

const STATUS_STYLE = {
  delivered: { color: GREEN, Icon: CheckCircle2, label: "Delivered" },
  filed:     { color: GREEN, Icon: CheckCircle2, label: "Delivered" },
  success:   { color: GREEN, Icon: CheckCircle2, label: "Delivered" },
  sending:   { color: NAVY,  Icon: Loader2,      label: "Sending",   spin: true },
  in_progress:{color: NAVY,  Icon: Loader2,      label: "In progress", spin: true },
  queued:    { color: NAVY,  Icon: Loader2,      label: "Queued",    spin: true },
  pending:   { color: NAVY,  Icon: Loader2,      label: "Pending",   spin: true },
  failed:    { color: RED,   Icon: AlertTriangle,label: "Failed" },
  error:     { color: RED,   Icon: AlertTriangle,label: "Error" },
  no_answer: { color: AMBER, Icon: AlertTriangle,label: "No answer" },
  busy:      { color: AMBER, Icon: AlertTriangle,label: "Busy" },
};

export default function SentList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [view, setView] = useViewMode("comms2-fax-sent-view", "list");

  const load = async () => {
    setLoading(true);
    try {
      const r = await faxAPI.listSent(80);
      setItems(Array.isArray(r) ? r : (r?.items || []));
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const retry = async (fax) => {
    setBusyId(fax.id);
    try { await faxAPI.retry(fax.id); toast.success("Retry queued"); load(); }
    catch (e) { toast.error(e.message || "Retry failed"); }
    finally { setBusyId(null); }
  };

  const canRetryFax = (f) => ["failed", "error", "no_answer", "no-answer", "busy"].includes(f.status);
  const fmtWhen = (f) => f.sent_at || f.created_at
    ? new Date(f.sent_at || f.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <div className="p-4">
      {loading && <div className="text-center text-[13px] text-slate-400 py-8"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading sent faxes…</div>}
      {!loading && items.length === 0 && <div className="text-center text-[13px] text-slate-400 py-8">No sent faxes yet.</div>}
      {items.length > 0 && (
        <>
          <div className="flex items-center justify-end mb-3">
            <ViewToggle view={view} setView={setView} testid="comms2-fax-sent" />
          </div>
          {view === "list" && (
        <div className="rounded-lg border overflow-hidden bg-white" style={{ borderColor: NAVY_HAIRLINE }}>
          <div className="grid grid-cols-[minmax(0,1fr)_130px_120px_140px] text-[10.5px] uppercase font-semibold tracking-wider text-slate-500 px-3 py-1.5 border-b" style={{ borderColor: NAVY_HAIRLINE, background: NAVY_TINT }}>
            <div>Recipient</div><div>Sent</div><div>Status</div><div className="text-right">Actions</div>
          </div>
          {items.map(f => {
            const st = STATUS_STYLE[f.status] || { color: NAVY, Icon: CheckCircle2, label: f.status || "?" };
            const Icon = st.Icon;
            const canRetry = canRetryFax(f);
            return (
              <div key={f.id} className="grid grid-cols-[minmax(0,1fr)_130px_120px_140px] items-center px-3 py-2 border-b last:border-0 text-[12px]" style={{ borderColor: NAVY_HAIRLINE }} data-testid={`comms2-fax-sent-${f.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate" style={{ color: NAVY }}>
                    {f.to_name || formatPhoneNumber(f.to_number) || "Unknown"}
                  </div>
                  <div className="text-[10.5px] text-slate-500 font-mono">{formatPhoneNumber(f.to_number)}</div>
                </div>
                <div className="text-[11px] text-slate-600 tabular-nums">{fmtWhen(f)}</div>
                <div className="flex items-center gap-1 text-[11.5px] font-medium" style={{ color: st.color }}>
                  <Icon className={`w-3.5 h-3.5 ${st.spin ? "animate-spin" : ""}`} />
                  {st.label}
                </div>
                <div className="flex items-center justify-end gap-1.5">
                  {canRetry && (
                    <button
                      onClick={() => retry(f)}
                      disabled={busyId === f.id}
                      className="h-7 px-2 rounded text-[11px] border font-medium hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1"
                      style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
                      data-testid={`comms2-fax-sent-retry-${f.id}`}
                    >
                      {busyId === f.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                      Retry
                    </button>
                  )}
                  <a
                    href={faxAPI.downloadSent(f.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-7 w-7 rounded border flex items-center justify-center hover:bg-slate-50"
                    style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
                    title="Download"
                    data-testid={`comms2-fax-sent-download-${f.id}`}
                  >
                    <Download className="w-3 h-3" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
          )}
          {view === "cards" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="comms2-fax-sent-cards">
              {items.map(f => {
                const st = STATUS_STYLE[f.status] || { color: NAVY, Icon: CheckCircle2, label: f.status || "?" };
                const Icon = st.Icon;
                const canRetry = canRetryFax(f);
                return (
                  <div key={f.id} className="rounded-xl bg-white border p-3" style={{ borderColor: NAVY_HAIRLINE }} data-testid={`comms2-fax-sent-card-${f.id}`}>
                    <div className="flex items-baseline gap-2">
                      <div className="text-[13px] font-semibold truncate flex-1" style={{ color: NAVY }}>
                        {f.to_name || formatPhoneNumber(f.to_number) || "Unknown"}
                      </div>
                      <div className="text-[10px] text-slate-400 tabular-nums shrink-0">{fmtWhen(f)}</div>
                    </div>
                    <div className="text-[11.5px] text-slate-600 mt-0.5 font-mono">{formatPhoneNumber(f.to_number)}</div>
                    <div className="mt-2 flex items-center gap-1 text-[12px] font-medium" style={{ color: st.color }}>
                      <Icon className={`w-3.5 h-3.5 ${st.spin ? "animate-spin" : ""}`} />
                      {st.label}
                    </div>
                    <div className="flex items-center gap-1.5 mt-2 pt-2 border-t" style={{ borderColor: NAVY_HAIRLINE }}>
                      {canRetry && (
                        <button
                          onClick={() => retry(f)}
                          disabled={busyId === f.id}
                          className="h-8 px-2.5 rounded-md border text-[11.5px] font-medium hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1"
                          style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
                          data-testid={`comms2-fax-sent-card-retry-${f.id}`}
                        >
                          {busyId === f.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                          Retry
                        </button>
                      )}
                      <a
                        href={faxAPI.downloadSent(f.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-auto h-8 w-8 rounded-md border flex items-center justify-center hover:bg-slate-50"
                        style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
                        title="Download"
                        data-testid={`comms2-fax-sent-card-download-${f.id}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
